import styled from 'styled-components'

import { CrownIcon, LightningIcon } from '@blade/ui-kit'

import type { PremiumMeta } from '@root/entities/premium'
import type { ClassNameProps } from '@root/shared/react'
import type { CSSProp } from 'styled-components'

type BuyPremiumIconProps = {
  product: PremiumMeta.PremiumProduct
  size?: number
  /** Defaults to a share of `size` that depends on the product. */
  sizeIcon?: number
  css?: CSSProp
  style?: React.CSSProperties
} & ClassNameProps

const INNER_RING_INSET_RATIO = 4.85 / 73
const INNER_RING_BORDER_RATIO = 1.6 / 73

// TODO: gradientFrom has no matching palette token.
const PRODUCT_STYLES: Record<
  PremiumMeta.PremiumProduct,
  {
    Icon: typeof CrownIcon
    iconSizeRatio: number
    iconColor: string
    gradientFrom: string
    gradientTo: string
    ringColor: string
    glow: string
  }
> = {
  subscription: {
    Icon: CrownIcon,
    iconSizeRatio: 33.49 / 73,
    iconColor: 'var(--subscriptionIcon-9)',
    gradientFrom: 'var(--subscriptionGradientFrom-9)',
    gradientTo: 'var(--subscriptionGradientTo-9)',
    ringColor: 'rgba(var(--subscriptionIcon-9-rgb), 0.15)',
    glow: 'var(--subscriptionGlow-5)',
  },
  boosters: {
    Icon: LightningIcon,
    iconSizeRatio: 0.5,
    iconColor: 'var(--boostIcon-9)',
    gradientFrom: 'var(--boostGradientFrom-9)',
    gradientTo: 'var(--boostGradientTo-9)',
    ringColor: 'rgba(var(--boostIcon-9-rgb), 0.15)',
    glow: 'var(--boostGlow-5)',
  },
}

export const BuyPremiumIcon = ({
  product,
  size = 73,
  sizeIcon,
  className,
  css,
  style,
}: BuyPremiumIconProps) => {
  const { Icon, iconSizeRatio, iconColor } = PRODUCT_STYLES[product]

  return (
    <Root
      $size={size}
      $product={product}
      className={className}
      css={css}
      style={style}
      aria-hidden
    >
      <Icon
        size={sizeIcon ?? Math.round(size * iconSizeRatio)}
        color={iconColor}
      />
      <InnerRing $size={size} $product={product} />
    </Root>
  )
}

const Root = styled.div<{
  $size: number
  $product: PremiumMeta.PremiumProduct
}>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  position: relative;
  display: grid;
  place-items: center;
  justify-self: center;
  flex-shrink: 0;
  isolation: isolate;

  background: linear-gradient(
    180deg,
    ${({ $product }) => PRODUCT_STYLES[$product].gradientFrom} 0%,
    ${({ $product }) => PRODUCT_STYLES[$product].gradientTo} 100%
  );
  box-shadow: 0 0 5px ${({ $product }) => PRODUCT_STYLES[$product].glow};
`

const InnerRing = styled.div<{
  $size: number
  $product: PremiumMeta.PremiumProduct
}>`
  position: absolute;
  inset: ${({ $size }) => $size * INNER_RING_INSET_RATIO}px;
  border-radius: 50%;
  border: ${({ $size }) => Math.max(1, $size * INNER_RING_BORDER_RATIO)}px solid
    ${({ $product }) => PRODUCT_STYLES[$product].ringColor};
  box-shadow: 0 1px 0 rgb(255 255 255 / 13%);
  pointer-events: none;
`
